import {Controller} from "@hotwired/stimulus";
import debounce from "lodash/debounce";

/**
 * TableStickyHeaderController mimics css position: sticky for table header.
 * Unfortunately, we can't use position: sticky because in Chrome this position type
 * creates unwanted artifacts - vertical white lines.
 *
 * So we have to invoke the necessary behavior using JS.
 *
 * The basic strategy is to create a clone of the real table header.
 * The position of the clone is fixed.
 *
 * There are the following stages during vertical table scrolling:
 * - the first row is in the user's viewport - the standard header is visible,
 *   the clone is hidden
 * - the first row is outside the user's viewport -
 *   the content of the standard header is invisible, the clone is visible
 * - only the clone and the last line remain in the user's viewport -
 *   the last line position detection is triggered
 *   and the clone scrolls smoothly with the last line
 * - the entire table is out of the user's viewport - the clone position is reset
 *
 * Application:
 * - enclose table in a div element:
 *    %div{data: { controller: 'table-sticky-header', table_sticky_header_invisible_class: 'invisible' }}
 *
 * So, for example:
 *  %div{data: { controller: 'table-sticky-header', table_sticky_header_invisible_class: 'invisible' }}
 *      %table.force-overflow-hidden{data: { table_sticky_header_target: 'table' }}
 *        %thead
 *          %tr
 *            %th
 *            %th
 *            %th...
 *        %tbody= rows
 *
 * Cooperation with HorizontalScrollController
 *
 * It is very likely that there will be a use of HorizontalScrollController around the table.
 *
 * It emits a special event 'horizontal-scroll', which we read here in TableStickyHeaderController
 * and use it to set the left position of the clone.
 *
 * It is VERY IMPORTANT if you use HorizontalScrollController and TableStickyHeaderController
 * for one table: these controllers MUST NOT be used in one div element, but must be in separate elements.
 *
 * HorizontalScrollController must be used first,
 * TableStickyHeaderController must be used in a child element this way:
 *
 * %div{data: { controller: 'horizontal-scroll' }}
 *   %div{data: { controller: 'table-sticky-header' }}
 *
 */


export default class extends Controller {
  static targets = ['headerCellContent'];

  table = null;
  tableHead = null;
  tableBody = null;

  tableClone = null;

  /*
    * The table header is cloned and placed in new table.
    * Save clone and its left position and height.
   */
  tableHeaderClone = null;
  tableHeaderCloneLeft = 0;
  tableHeaderCloneHeight = 0;

  firstRowDetector = null;
  lastRowDetector = null;

  /* Detect last row and its height.  */
  lastRow = null;
  lastRowHeight = 0;

  /* Detect scroll direction */
  scrollDirection = 'down';

  /* Check if the controller is inside a dialog. */
  dialogContainer = null;

  connect() {
    if (this.element.dataset.controller.includes('horizontal-scroll')) {
      console.error('TableStickyHeaderController must not be used in the same element as HorizontalScrollController');
    }

    this._initAll();

    this.horizontalScrollListener = this._listenToHorizontalScroll.bind(this);
    window.addEventListener('horizontal-scroll', this.horizontalScrollListener);

    this.resizeListener = this._reinit.bind(this)
    window.addEventListener("resize", debounce(this.resizeListener, 100));

    this.leftPanelResizeListener = this._reinit.bind(this);
    window.addEventListener("left-panel-change", this.leftPanelResizeListener);
  }

  disconnect() {
    super.disconnect();
    this.observer.disconnect();
    this._cleanUpPossibleDuplicates();
    const verticalScrollListener = this._listenToVerticalScroll;
    window.removeEventListener('scroll', verticalScrollListener);
    window.removeEventListener('horizontal-scroll', this.horizontalScrollListener);
  }

  _initAll() {
    this._markTargets();
    this._insertFirstRowDetector();
    this._insertLastRowDetector();

    this._cloneHeader();
    this._setLastRow()
    this._initObserver();
    this._checkForParentWithHorizontalScroll();
  }

  _reinit() {
    this._cleanUpPossibleDuplicates();
    this._initAll();
  }

  _markTargets() {
    this.table = this.element.querySelector('table');
    this.tableHead = this.table.querySelector('thead');
    this.tableBody = this.table.querySelector('tbody');
    this.dialogContainer = this.element.closest('dialog');
  }

  _insertFirstRowDetector() {
    const firstRowDetectorElement = document.createElement('div');
    firstRowDetectorElement.id = 'firstRowDetector';
    firstRowDetectorElement.classList.add('relative');
    this.firstRowDetector = this.element.insertBefore(firstRowDetectorElement, this.table);
  }

  _insertLastRowDetector() {
    const lastRowDetectorElement = document.createElement('div');
    lastRowDetectorElement.id = 'lastRowDetector';
    lastRowDetectorElement.classList.add('relative');
    this.lastRowDetector = this.element.appendChild(lastRowDetectorElement);
  }

  /* When controller reconnects,
     we need to remove possible elements duplicates */
  _cleanUpPossibleDuplicates() {
    this.firstRowDetector && this.firstRowDetector.remove();
    this.lastRowDetector && this.lastRowDetector.remove();
    this.tableClone && this.tableClone.remove();
  }

  /* Listen to horizontal scroll from HorizontalScrollController
     and set the left position of the cloned header.
     To select the correct elements, we need to read the container from the event
     and work with the elements in that container.*/
  _listenToHorizontalScroll(e) {
    const tableHeaderClone = e.detail.container.querySelector('.sticky-table-header');
    const firstRowDetector = e.detail.container.querySelector('#firstRowDetector');
    const lastRowDetector = e.detail.container.querySelector('#lastRowDetector');
    tableHeaderClone.style.left = `${this.tableHeaderCloneLeft - e.detail.scrollLeft}px`;
    firstRowDetector.style.left = `${e.detail.scrollLeft}px`;
    lastRowDetector.style.left = `${e.detail.scrollLeft}px`;
  }

  _initObserver = () => {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (this._isFirstRow(entry)) {
            this._firstRowEntryChanged(entry);
          }
          if (this._isLastRow(entry)) {
            this._detectScrollDirection(entry);
            this._lastRowEntryChanged(entry)
          }
        });
      },
    );
    this._connectObserver();
  }

  _connectObserver() {
    this.observer.observe(this.table);
    this.observer.observe(this.firstRowDetector);
    this.observer.observe(this.lastRowDetector);
  }

  _firstRowEntryChanged(entry) {
    if (entry.isIntersecting) {
      this._unstickHeader();
    } else {
      this._stickHeader();
    }
  }

  /* In a situation where there is only the tableHeader clone
     and the last row left to scroll, we need to synchronize
     the top position of the clone with the last row. */
  _lastRowEntryChanged(entry) {
    const verticalScrollListener = this._listenToVerticalScroll;

    if (entry.isIntersecting) {
      if (this.scrollDirection === 'up') {
        window.removeEventListener('scroll', verticalScrollListener);
      } else {
        this._stickHeader();
      }
    } else {
      if (this.scrollDirection === 'down') {
        window.addEventListener('scroll', verticalScrollListener);
      }
    }

    this._resetClonePosition();
  }

  /* The header is sticked to the top of the viewport
     when the first row is not in the viewport. */
  _stickHeader() {
    this.tableHead.classList.add('invisible');
    this.tableHeaderClone.classList.remove('invisible');
  }

  /* The header is unsticked when the first row is in the viewport. */
  _unstickHeader() {
    this.tableHeaderClone.classList.add('invisible');
    this.tableHead.classList.remove('invisible');
  }

  _isFirstRow(entry) {
    return entry.target === this.firstRowDetector;
  }

  _isLastRow(entry) {
    return entry.target === this.lastRowDetector;
  }

  /* Find the last row of the table body, get its height
     and set the top position of the last row detector. */
  _setLastRow() {
    this.lastRow = this.tableBody.querySelector('tr:last-child');
    this.lastRowHeight = this.lastRow ? this.lastRow.offsetHeight : 0;
    const tableMarginBottom = parseInt(getComputedStyle(this.table).marginBottom);
    this.lastRowDetector.style.top = (this.lastRowHeight + this.tableHeaderCloneHeight + tableMarginBottom) * -1 + 'px';
  }

  /* Clone the table header - we need this clone not to distort the height of the table.
     The clone is placed in new table. */
  _cloneHeader() {
    let rightPadding = '0px';
    const paddingSource = document.querySelector('main');
    if (paddingSource) rightPadding = getComputedStyle(paddingSource).paddingRight;

    const header = this.tableHead;

    this.tableHeaderClone = header.cloneNode(true);
    this._adjustHeaderClone();
    this._sizeCloneElements();


    const tableClassList = this.table.classList

    /* Create a new table and append the clone to it. */
    const table = document.createElement('table');

    table.appendChild(this.tableHeaderClone);
    table.classList.add('appended-table');
    table.classList.add(...tableClassList);
    table.style.width = `${this.table.offsetWidth}px`;

    /* Append the new table to controller element. */
    this.tableClone = this.element.appendChild(table);

    const headerCells = this.tableHead.querySelectorAll('th');
    const cloneHeaderCells = this.tableClone.querySelectorAll('th');

    headerCells.forEach((cell, index) => {
      const width = cell.getBoundingClientRect().width;
      const height = cell.getBoundingClientRect().height;
      cloneHeaderCells[index].style.width = `${width}px`;
      cloneHeaderCells[index].style.height = `${height}px`;
    })

    const stickyElement = this.tableClone.querySelector('.sticky');
    if (stickyElement) stickyElement.style.right = rightPadding;
  }

  /* Set clone classes and left position.  */
  _adjustHeaderClone() {
    const {left: headerLeft} = this.tableHead.getBoundingClientRect();

    this.tableHeaderClone.classList.add('sticky-table-header', 'invisible');
    this.tableHeaderClone.style.left = `${headerLeft}px`;
    this.tableHeaderCloneLeft = headerLeft;
  }

  /* Set the width and height of the elements in the header clone cells
     so that the header clone matches the source. */
  _sizeCloneElements() {
    const headerCellElements = this.headerCellContentTargets;
    const cloneHeaderCellElements = this.tableHeaderClone.querySelectorAll('[data-table-sticky-header-target="headerCellContent"]');

    headerCellElements.forEach((cellElement, index) => {
      let cellBackgroundColor = cellElement.style.backgroundColor;
      if (!cellBackgroundColor || cellBackgroundColor === 'transparent') {
        cellBackgroundColor = '#fff';
      }

      const width = cellElement.offsetWidth;
      const height = cellElement.offsetHeight;

      cellElement.closest('th').style.backgroundColor = cellBackgroundColor;
      cloneHeaderCellElements[index].style.width = `${width}px`;
      cloneHeaderCellElements[index].style.height = `${height}px`;
    })

    this.tableHeaderCloneHeight = this.tableHead.offsetHeight;
  }

  /* If this element has a parent with horizontal scroll controller,
     we need to add force-overflow-hidden class to this element
     to show only necessary part of sticked table header. */
  _checkForParentWithHorizontalScroll() {
    const parentWithHorizontalScroll = this.element.closest('[data-controller="horizontal-scroll"]');
    if (parentWithHorizontalScroll) {
      parentWithHorizontalScroll.classList.add('force-overflow-hidden');
    }
  };

  /* Detect scroll direction - we need this
     to set the behaviour of the clone. */
  _detectScrollDirection(entry) {
    const currentY = entry.boundingClientRect.y;

    if (currentY <= 0) {
      this.scrollDirection = 'down';
    } else {
      this.scrollDirection = 'up';
    }
  }

  /* Reset the top position of the clone */
  _resetClonePosition() {
    let top = 0;
    if (this.dialogContainer) {
      top = this.dialogContainer.getBoundingClientRect().top;
    }
    this.tableHeaderClone.style.top = `${top}px`;
  }

  /* Set the top position of the clone to the last row */
  _listenToVerticalScroll = () => {
    this.tableHeaderClone.style.top = `${this.lastRowDetector.getBoundingClientRect().top}px`;
  }
}